import { Link } from "react-router";
import type { ActorDTO } from "@roomate/core";
import { ApiError, useAgentCorrectionRules, useAgentCorrections, useMe, usePinLesson, useSetLessonRetired } from "../api.ts";
import { Action, Block, Disclosure, Pill } from "../calm.tsx";
import {
  CORRECTION_KIND_LABEL,
  CORRECTION_STATUS_LABEL,
  RULE_STATUS_LABEL,
  lessonRef,
  ruleStatus,
  hasGoneLive,
  repeatsText,
  runsSinceLiveText,
  unturnedCorrections,
  type CorrectionDTO,
  type CorrectionRuleDTO,
  type LessonDTO,
} from "../lessons.ts";
import { Ago, ActorName, cx, ErrorNote } from "../ui.tsx";

/** Pin and Retire are for people who can write; viewers and agents see no buttons (AC2). */
export function canGovernLessons(me: Pick<ActorDTO, "kind" | "access"> | undefined): boolean {
  return me?.kind === "human" && me.access !== "viewer";
}

/**
 * Pin (on probation) and Retire (not yet retired) for one lesson, through the existing
 * `/api/lessons/:id/pin|retire` routes. Renders nothing for someone who cannot write.
 */
export function LessonGovernButtons({ lesson, testIdPrefix = "lesson" }: { lesson: Pick<LessonDTO, "id" | "status" | "live">; testIdPrefix?: string }) {
  const me = useMe().data;
  const pin = usePinLesson();
  const retire = useSetLessonRetired();
  if (!canGovernLessons(me) || lesson.status !== "active") return null;
  const probation = !lesson.live;
  const busy = pin.isPending || retire.isPending; // one request at a time per lesson
  return (
    <div className="flex shrink-0 flex-col items-end gap-1.5">
      <div className="flex items-center gap-1.5">
        {probation && (
          <Action testId={`${testIdPrefix}-pin`} label={`Pin rule ${lessonRef(lesson.id)}`} disabled={busy} onClick={() => pin.mutate(lesson.id)}>
            {pin.isPending ? "Pinning…" : "Pin"}
          </Action>
        )}
        <Action testId={`${testIdPrefix}-retire`} label={`Retire rule ${lessonRef(lesson.id)}`} danger disabled={busy} onClick={() => retire.mutate({ id: lesson.id, retire: true })}>
          {retire.isPending ? "Retiring…" : "Retire"}
        </Action>
      </div>
      {(pin.error || retire.error) && (
        <div className="max-w-[34ch] text-right">
          <ErrorNote error={pin.error ?? retire.error} />
        </div>
      )}
    </div>
  );
}

/** The section on an agent's profile: rules learned from corrections (spec §2.6). Agents only. */
export function CorrectionRules({ agentId, actors }: { agentId: string; actors: ActorDTO[] }) {
  const me = useMe().data;
  const rules = useAgentCorrectionRules(agentId);
  // The list of corrections is people only (spec §4); an agent looking at itself skips it.
  const people = me?.kind === "human";
  const corrections = useAgentCorrections(agentId, people);
  if (rules.error instanceof ApiError && (rules.error.status === 404 || (rules.error.status === 403 && me?.id !== agentId))) return null; // older hub, or not ours to read
  return (
    <Block title="Rules learned from corrections" aside={rules.data ? `${rules.data.length}` : undefined} testId="correction-rules">
      {rules.error ? (
        <ErrorNote error={rules.error} />
      ) : !rules.data ? (
        <p className="text-[13.5px] text-ink-3">Loading…</p>
      ) : (
        <CorrectionRulesView rules={rules.data} corrections={corrections.data} actors={actors} />
      )}
      {corrections.error && !(corrections.error instanceof ApiError && corrections.error.status === 404) && <ErrorNote error={corrections.error} />}
    </Block>
  );
}

/** Presentation only, so it can be rendered with fixed data. Buttons come from {@link LessonGovernButtons}. */
export function CorrectionRulesView({ rules, corrections, actors, govern = true }: { rules: CorrectionRuleDTO[]; corrections?: CorrectionDTO[]; actors: ActorDTO[]; govern?: boolean }) {
  const unturned = corrections ? unturnedCorrections(corrections) : [];
  return (
    <div className="space-y-4">
      {rules.length === 0 ? (
        <p className="text-[13.5px] text-ink-3" data-testid="correction-rules-empty">
          No rules yet. When a reviewer sends this agent's work back, a rule is drafted from it; a second, independent correction makes it live.
        </p>
      ) : (
        <ul className="divide-y divide-rule/60" data-testid="correction-rules-list">
          {rules.map((r) => (
            <RuleRow key={r.lesson.id} rule={r} actors={actors} govern={govern} />
          ))}
        </ul>
      )}
      {unturned.length > 0 && (
        <Disclosure summary={`${unturned.length} correction${unturned.length === 1 ? "" : "s"} not turned into rules`} testId="corrections-unturned">
          <ul className="mt-2 space-y-1 text-[13px] text-ink-2">
            {unturned.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-2" data-correction-status={c.status}>
                <Link to={`/tasks/${c.taskId}`} className="underline decoration-rule hover:text-ink">
                  {c.taskNumber ? `#${c.taskNumber}` : "task"}
                </Link>
                <span>{CORRECTION_KIND_LABEL[c.kind]}</span>
                <span aria-hidden>·</span>
                <Pill>{CORRECTION_STATUS_LABEL[c.status]}</Pill>
                <span aria-hidden>·</span>
                <Ago ts={c.createdAt} />
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
    </div>
  );
}

function RuleRow({ rule, actors, govern }: { rule: CorrectionRuleDTO; actors: ActorDTO[]; govern: boolean }) {
  const { lesson } = rule;
  const status = ruleStatus(lesson);
  const repeats = rule.recurrencesSinceLive;
  return (
    <li className="py-4 first:pt-0" data-testid="correction-rule" data-rule-status={status} data-lesson-id={lesson.id}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <p className="whitespace-pre-wrap break-words text-[14px] leading-relaxed">{lesson.text}</p>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-3">
            <Pill tone={status === "live" ? "signal" : "neutral"}>
              <span data-testid="rule-status">{RULE_STATUS_LABEL[status]}</span>
            </Pill>
            {lesson.ruleKey && <span className="font-mono text-ink-2" data-testid="rule-key">{lesson.ruleKey}</span>}
            <span className="font-mono">{lessonRef(lesson.id)}</span>
          </p>
          <p className="mt-1.5 text-[12.5px] text-ink-3" data-testid="rule-runs">
            {!hasGoneLive(lesson) ? (
              status === "retired" ? "Retired before it went live: never given to a run." : status === "live" ? "Run counts start once the rule is recorded as live." : "Not live yet: not given to any run."
            ) : (
              <>
                {runsSinceLiveText(rule.runsSinceLive)} ·{" "}
                <span className={cx(repeats > 0 && "font-medium text-high")} data-testid="rule-repeats" data-repeats-hot={repeats > 0 ? "true" : undefined}>
                  {repeatsText(repeats)}
                </span>
              </>
            )}
          </p>
          <ul className="mt-2 space-y-1 text-[12.5px] text-ink-2" data-testid="rule-sources">
            {rule.sources.map((s) => (
              <li key={s.correctionId} className="flex flex-wrap items-center gap-x-2">
                <Link to={`/tasks/${s.taskId}`} className="underline decoration-rule hover:text-ink">
                  #{s.taskNumber}
                </Link>
                <span>{CORRECTION_KIND_LABEL[s.kind]}</span>
                <span aria-hidden>·</span>
                <span>
                  by <ActorName id={s.answeredBy} actors={actors} />
                </span>
                <span aria-hidden>·</span>
                <Ago ts={s.answeredAt} />
                {s.recurrence && (
                  <span className="rounded-full bg-high/10 px-2 py-0.5 text-[11.5px] text-high" data-testid="rule-repeat-mark">
                    repeat
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        {govern && <LessonGovernButtons lesson={lesson} testIdPrefix="rule" />}
      </div>
    </li>
  );
}
